/**
 * Daily Close Wizard - Multi-step bolt zarasi varazslo
 * Epic 61: Admin Kozpont Dashboard (Story 61-4)
 */

import { postDailyClose, type AdminSummary } from '@/api/admin-center';
import { Button } from '@/components/ui';
import { useCloseSession, useCurrentSession } from '@/hooks/pos';
import { useWarehouseGate } from '@/hooks/use-warehouse-gate';
import { cn, formatHuf } from '@/lib/utils';
import type { ZReport } from '@/types/pos.types';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

interface DailyCloseWizardProps {
  summary: AdminSummary | null;
  onClose: () => void;
  onComplete: (zReport?: ZReport) => void;
}

const STEP_KEYS = [
  'admin.stepCashBalanceClose',
  'admin.stepOpenRentals',
  'admin.stepServiceStatuses',
  'admin.stepDailyTurnover',
  'admin.stepTaskClose',
  'admin.stepSummary',
] as const;

export function DailyCloseWizard({ summary, onClose, onComplete }: DailyCloseWizardProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [expectedCashBalance, setExpectedCashBalance] = useState<number>(0);
  const [actualCashBalance, setActualCashBalance] = useState<number>(0);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // POS session hooks — close the cash register when daily close is submitted
  const closeSessionMutation = useCloseSession();
  // Q-T167: a raktárat a JWT tenantId-jából oldjuk fel (GET /warehouses/default), NEM egy
  // beégetett UUID-ból. A korábbi `user as { warehouseId?: string }` cast MINDIG undefined-ot
  // adott (az AuthUser típusban nincs ilyen mező) → mindig a fallbackra esett, ami a KGC Demo
  // Tenant „Központi Raktár"-a → a valós tenantokon IDEGEN tenant raktár-UUID-jával dolgoztunk.
  // Következmény: a `currentSession` sosem lett OPEN → a záró ág NÉMÁN kimaradt (a Kisgépcentrum
  // Érd 1 munkamenete 2026-07-07 óta nyitva ragadt). A hook a POS-oldalon már bevált mintája.
  const { warehouseId, isBlocked, blockMessage } = useWarehouseGate();
  const locationId = warehouseId ?? '';
  const { data: currentSession } = useCurrentSession(locationId);

  const cashDifference = actualCashBalance - expectedCashBalance;

  const handleNext = () => {
    if (currentStep < STEP_KEYS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    // Q-T167 FAIL-LOUD: raktár nélkül a kassza-zárás NÉMÁN kimaradna (a régi viselkedés — ez
    // ragasztotta nyitva az Érd-tenant munkamenetét 3 hétig). Egy „napi zárás", ami nem zárja a
    // kasszát, nem napi zárás → inkább álljunk meg látható hibával, mint hogy sikert jelentsünk.
    if (isBlocked) {
      setSubmitError(blockMessage);
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // 1. Record daily close in admin-center
      await postDailyClose({
        expectedCashBalance,
        actualCashBalance,
        cashDifference,
        notes: notes || undefined,
      });

      // 2. Close POS cash register session and capture Z-report
      let zReport: ZReport | undefined;
      if (currentSession?.status === 'OPEN' && locationId) {
        try {
          zReport = await closeSessionMutation.mutateAsync({
            sessionId: currentSession.id,
            locationId,
            dto: {
              closingBalance: actualCashBalance,
              varianceNote: notes || undefined,
            },
          });
        } catch (posErr) {
          // POS session close failure is non-fatal
          console.warn('POS session close skipped:', posErr);
        }
      }

      onComplete(zReport);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('common.error');
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">{t('admin.cashBalanceDesc')}</p>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('admin.expectedBalanceHuf')}
              </label>
              <input
                type="number"
                value={expectedCashBalance || ''}
                onChange={e => setExpectedCashBalance(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-lg font-semibold text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                placeholder="0"
                min={0}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('admin.actualBalanceHuf')}
              </label>
              <input
                type="number"
                value={actualCashBalance || ''}
                onChange={e => setActualCashBalance(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-lg font-semibold text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                placeholder="0"
                min={0}
              />
            </div>
            {/* Difference display */}
            <div
              className={cn(
                'rounded-lg border p-4',
                cashDifference !== 0
                  ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                  : 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
              )}
            >
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    'font-medium',
                    cashDifference !== 0
                      ? 'text-red-700 dark:text-red-400'
                      : 'text-green-700 dark:text-green-400'
                  )}
                >
                  {t('admin.difference')}
                </span>
                <span
                  className={cn(
                    'text-xl font-bold',
                    cashDifference !== 0 ? 'text-red-600' : 'text-green-600'
                  )}
                >
                  {cashDifference > 0 ? '+' : ''}
                  {formatHuf(cashDifference)}
                </span>
              </div>
              {cashDifference !== 0 && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {t('admin.cashMismatch')}
                </p>
              )}
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">{t('admin.openRentalsReview')}</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">
                  {t('admin.expiredRentalsLabel')}
                </span>
                <span
                  className={cn(
                    'text-xl font-bold',
                    (summary?.overdueRentals.count ?? 0) > 0 ? 'text-red-600' : 'text-green-600'
                  )}
                >
                  {summary?.overdueRentals.count ?? 0} {t('admin.pcs')}
                </span>
              </div>
              {(summary?.overdueRentals.count ?? 0) > 0 && (
                <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
                  <span className="text-amber-700 dark:text-amber-400">
                    {t('admin.oldestRental')}
                  </span>
                  <span className="font-bold text-amber-600">
                    {t('admin.expiredDaysAgo', { days: summary?.overdueRentals.oldestDays ?? 0 })}
                  </span>
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">{t('admin.serviceCloseStatus')}</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">{t('admin.waitingLabel')}</span>
                <span className="text-xl font-bold text-amber-600">
                  {summary?.serviceWorksheets.pending ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">
                  {t('admin.inProgressLabel')}
                </span>
                <span className="text-xl font-bold text-blue-600">
                  {summary?.serviceWorksheets.inProgress ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">
                  {t('admin.todayCompleted')}
                </span>
                <span className="text-xl font-bold text-green-600">
                  {summary?.serviceWorksheets.completed ?? 0}
                </span>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">{t('admin.dailyTurnoverSummary')}</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">
                  {t('admin.todayBookingsLabel')}
                </span>
                <span className="text-xl font-bold text-green-600">
                  {summary?.todayBookings.count ?? 0} {t('admin.pcs')}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">
                  {t('admin.receivablesLabel')}
                </span>
                <span
                  className={cn(
                    'text-xl font-bold',
                    (summary?.receivables.totalAmount ?? 0) > 0
                      ? 'text-amber-600'
                      : 'text-green-600'
                  )}
                >
                  {formatHuf(summary?.receivables.totalAmount ?? 0)}
                </span>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">{t('admin.taskReviewClose')}</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">{t('admin.assignedTasks')}</span>
                <span className="text-xl font-bold text-blue-600">
                  {summary?.tasks.assigned ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">
                  {t('admin.expiredTasksLabel')}
                </span>
                <span
                  className={cn(
                    'text-xl font-bold',
                    (summary?.tasks.overdue ?? 0) > 0 ? 'text-red-600' : 'text-green-600'
                  )}
                >
                  {summary?.tasks.overdue ?? 0}
                </span>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('admin.noteOptional')}
              </label>
              <textarea
                rows={3}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
                placeholder={t('admin.notePlaceholder')}
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">{t('admin.verifyAndCloseStore')}</p>
            <div className="space-y-3">
              {/* Cash balance summary */}
              <div
                className={cn(
                  'rounded-lg border p-4',
                  cashDifference !== 0
                    ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                    : 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">{t('admin.cashBalance')}</span>
                  <div className="text-right">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {t('admin.expectedActual', {
                        expected: formatHuf(expectedCashBalance),
                        actual: formatHuf(actualCashBalance),
                      })}
                    </div>
                    <div
                      className={cn(
                        'text-lg font-bold',
                        cashDifference !== 0 ? 'text-red-600' : 'text-green-600'
                      )}
                    >
                      {t('admin.deviation', {
                        amount: (cashDifference > 0 ? '+' : '') + formatHuf(cashDifference),
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">
                  {t('admin.openRentalsLabel')}
                </span>
                <span
                  className={cn(
                    'font-bold',
                    (summary?.overdueRentals.count ?? 0) > 0 ? 'text-red-600' : 'text-green-600'
                  )}
                >
                  {summary?.overdueRentals.count ?? 0} {t('admin.pcs')}
                </span>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">
                  {t('admin.serviceWorksheetsOpen')}
                </span>
                <span className="font-bold text-blue-600">
                  {(summary?.serviceWorksheets.pending ?? 0) +
                    (summary?.serviceWorksheets.inProgress ?? 0)}{' '}
                  {t('admin.pcs')}
                </span>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">
                  {t('admin.receivablesLabel')}
                </span>
                <span
                  className={cn(
                    'font-bold',
                    (summary?.receivables.totalAmount ?? 0) > 0
                      ? 'text-amber-600'
                      : 'text-green-600'
                  )}
                >
                  {formatHuf(summary?.receivables.totalAmount ?? 0)}
                </span>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                <span className="text-gray-700 dark:text-gray-300">
                  {t('admin.tasksExpiredAssigned')}
                </span>
                <span className="font-bold text-blue-600">
                  {summary?.tasks.overdue ?? 0} / {summary?.tasks.assigned ?? 0}
                </span>
              </div>

              {notes && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.noteLabel')}
                  </span>
                  <p className="mt-1 text-gray-600 dark:text-gray-400">{notes}</p>
                </div>
              )}
            </div>
            {submitError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                {submitError}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="daily-close-wizard-title"
      onKeyDown={e => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div className="kgc-card-bg max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-200 p-6 dark:border-gray-700">
          <div>
            <h2
              id="daily-close-wizard-title"
              className="text-xl font-semibold text-gray-900 dark:text-white"
            >
              {t('admin.dailyCloseWizard')}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t(STEP_KEYS[currentStep] ?? '')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted dark:hover:text-muted-foreground"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Step Indicator */}
        <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2">
            {STEP_KEYS.map((stepKey, index) => (
              <div key={stepKey} className="flex items-center gap-2">
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors',
                    index < currentStep
                      ? 'bg-green-500 text-white'
                      : index === currentStep
                        ? 'bg-primary text-white'
                        : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                  )}
                >
                  {index < currentStep ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                {index < STEP_KEYS.length - 1 && (
                  <div
                    className={cn(
                      'h-0.5 w-6 transition-colors',
                      index < currentStep ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                    )}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* [CR] Botka: false-affordance-őr — ha a raktár NEM oldható fel, ezt a wizard
            ELEJÉN mondjuk meg, ne a submitnál. Enélkül a user végigtölti az űrlapot,
            és a legdrágább ponton tudja meg, hogy eleve nem tud zárni/nyitni. */}
        {isBlocked && (
          <div className="mx-6 mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            {blockMessage}
          </div>
        )}

        {/* Content */}
        <div className="p-6">{renderStepContent()}</div>

        {/* Footer */}
        <div className="flex justify-between border-t border-gray-200 p-6 dark:border-gray-700">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 0}>
            {t('admin.back')}
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            {currentStep < STEP_KEYS.length - 1 ? (
              <Button onClick={handleNext}>{t('common.next')}</Button>
            ) : (
              <Button
                onClick={() => void handleSubmit()}
                loading={isSubmitting}
                // [CR] Botka: false-affordance — raktár nélkül a zárás/nyitás BIZTOSAN nem
                // sikerülhet, ezért ne is legyen kattintható. A fail-loud üzenet marad
                // (handleSubmit), de a user ne a legvégén tudja meg.
                disabled={isBlocked}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {t('admin.closeStore')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
